import UserDetailModal from "../../components/Modals/UserDetailModal";
import React, { useEffect, useMemo, useState } from "react";
import { Input, Select, Button, Spin, Modal, Table, Typography } from "antd";
import {
  TableOutlined,
  AppstoreOutlined,
  BarsOutlined,
} from "@ant-design/icons";

import style from "./GalereyaUserList.module.css";
import { formatUserListTable } from "../../models/userListModel/utils";

const { Option } = Select;
const { Text } = Typography;

const weekdayMap = {
  Monday: "Пн",
  Tuesday: "Вт",
  Wednesday: "Ср",
  Thursday: "Чт",
  Friday: "Пт",
  Saturday: "Сб",
  Sunday: "Вс",
};

const GalereyaUserList = () => {
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [viewType, setViewType] = useState("cards");

  const placeId = "glra12a4a6";
  // ✳ Add these useState hooks inside your component:
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);

  const openUserModal = (student) => {
    setSelectedUser(student);
    setIsUserModalVisible(true);
  };

  const closeUserModal = () => {
    setIsUserModalVisible(false);
    setSelectedUser(null);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          "https://5zbcsztftk.execute-api.eu-west-1.amazonaws.com/default/prod-user-list-api",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sort_key: placeId,
              emp_category: "Сотрудник",
            }),
          }
        );
        const result = await response.json();
        const parsed = JSON.parse(result.body);
        const formatted = formatUserListTable(parsed);
        setUserList(formatted);

        const uniqueOffices = [
          ...new Set(formatted.map((user) => user.emp_office).filter(Boolean)),
        ];
        setDepartmentsList(uniqueOffices);
      } catch (e) {
        console.error("Ошибка загрузки:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSearch = (e) => setSearchTerm(e.target.value.toLowerCase());

  const filteredAndSortedUsers = useMemo(() => {
    return userList
      .filter((user) =>
        `${user.user_name} ${user.user_surname}`
          .toLowerCase()
          .includes(searchTerm)
      )
      .filter((user) => {
        return selectedOffices.length === 0
          ? true
          : selectedOffices.includes(user.emp_office);
      })
      .filter((user) => user.emp_category !== "Уволен")
      .sort((a, b) =>
        sortOrder === "asc"
          ? a.user_surname.localeCompare(b.user_surname, "ru")
          : b.user_surname.localeCompare(a.user_surname, "ru")
      );
  }, [userList, searchTerm, selectedOffices, sortOrder]);

  const columns = [
    { title: "Имя", dataIndex: "user_name", key: "user_name" },
    { title: "Фамилия", dataIndex: "user_surname", key: "user_surname" },
    { title: "Офис", dataIndex: "emp_office", key: "emp_office" },
    { title: "Подразделение", dataIndex: "emp_unit", key: "emp_unit" },
    { title: "Тип работы", dataIndex: "work_type", key: "work_type" },
    {
      title: "Рабочие дни",
      dataIndex: "work_days",
      key: "work_days",
      render: (days) =>
        Array.isArray(days)
          ? days.map((d) => weekdayMap[d] || d).join(", ")
          : "Не указано",
    },
    { title: "Должность", dataIndex: "emp_position", key: "emp_position" },
    {
      title: "Пол",
      dataIndex: "user_gender",
      key: "user_gender",
      render: (g) => (g === "Male" ? "М" : "Ж"),
    },
    {
      title: "Начало дня",
      dataIndex: "start_work_time",
      key: "start_work_time",
    },
    { title: "Конец дня", dataIndex: "end_work_time", key: "end_work_time" },
  ];

  return (
    <div className={style.userList_wrapper}>
      <div className={style.counts_block}>
        <Text strong style={{ fontSize: "18px" }}>
          Всего сотрудников: {filteredAndSortedUsers.length}
        </Text>
        {selectedOffices.length > 0 && (
          <Text
            style={{
              display: "block",
              marginTop: 6,
              fontSize: "14px",
              color: "#666",
            }}
          >
            Офисы: {selectedOffices.join(", ")}
          </Text>
        )}
        <h1 className={style.userList_title}>Сотрудники</h1>
      </div>

      <div className={style.user_list_top_wrapper}>
        <div className={style.top_buttons_wrapper}>
          <Input
            placeholder="Поиск по имени или фамилии"
            value={searchTerm}
            onChange={handleSearch}
            style={{ width: "300px", marginRight: "10px" }}
          />
          <Button
            onClick={() => setSortOrder("asc")}
            type={sortOrder === "asc" ? "primary" : "default"}
          >
            А-Я
          </Button>
          <Button
            onClick={() => setSortOrder("desc")}
            type={sortOrder === "desc" ? "primary" : "default"}
          >
            Я-А
          </Button>
          <Select
            mode="multiple"
            placeholder="Фильтр по офисам"
            value={selectedOffices}
            onChange={setSelectedOffices}
            allowClear
            style={{ width: "300px" }}
            loading={loading}
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
          >
            {departmentsList.map((dept) => (
              <Option key={dept} value={dept}>
                {dept}
              </Option>
            ))}
          </Select>
          <div className={style.view_switch_buttons}>
            <Button
              icon={<AppstoreOutlined />}
              type={viewType === "cards" ? "primary" : "default"}
              shape="default"
              onClick={() => setViewType("cards")}
              className={viewType === "cards" ? style.active_view_button : ""}
            />
            <Button
              icon={<BarsOutlined />}
              type={viewType === "table" ? "primary" : "default"}
              shape="default"
              onClick={() => setViewType("table")}
              className={viewType === "table" ? style.active_view_button : ""}
            />
          </div>
        </div>
      </div>

      <div className={style.table_or_cards_wrapper}>
        {loading ? (
          <div style={{ padding: "50px" }}>
            <Spin tip="Загрузка..." size="large" />
          </div>
        ) : viewType === "table" ? (
          <Table
            dataSource={filteredAndSortedUsers}
            columns={columns}
            rowKey="partition_key"
            pagination={{ pageSize: 10 }}
            bordered
            className={style.userList_table}
          />
        ) : (
          <div className={style.cards_wrapper}>
            {filteredAndSortedUsers.map((user) => (
              // Inside GalereyaUserList.js, replace the card JSX inside `filteredAndSortedUsers.map` with the following:

              <div key={user.partition_key} className={style.card}>
                <div className={style.card_header}>
                  <div className={style.card_image}>
                    <img
                      src={user.presigned_url || "/default_user_image.png"}
                      alt="User"
                      onClick={() =>
                        Modal.info({
                          title: "Просмотр изображения",
                          content: (
                            <img
                              src={
                                user.presigned_url || "/default_user_image.png"
                              }
                              alt="Full"
                              style={{
                                width: "100%",
                                maxHeight: "80vh",
                                objectFit: "contain",
                              }}
                            />
                          ),
                          width: "50vw",
                          centered: true,
                        })
                      }
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default_user_image.png";
                      }}
                    />
                  </div>
                  <div className={style.card_header_text}>
                    <h3
                      className={style.card_title}
                      onClick={() => openUserModal(user)}
                    >{`${user.user_name} ${user.user_surname}`}</h3>
                    <div className={style.card_position_label}>Должность:</div>
                    <div className={style.card_position_value}>
                      {user.emp_position}
                    </div>
                  </div>
                </div>

                <div className={style.card_divider}></div>

                <div className={style.card_body}>
                  <div>
                    <div className={style.card_label}>Офис:</div>
                    <div className={style.card_value}>
                      {user.emp_office || "Не указано"}
                    </div>
                  </div>
                  <div>
                    <div className={style.card_label}>Подразделение:</div>
                    <div className={style.card_value}>
                      {user.emp_unit || "Не указано"}
                    </div>
                  </div>
                  <div>
                    <div className={style.card_label}>Тип работы:</div>
                    <div className={style.card_value}>
                      {user.work_type || "Не указано"}
                    </div>
                  </div>
                  <div>
                    <div className={style.card_label}>Рабочие дни:</div>
                    <div className={style.card_value}>
                      {Array.isArray(user.work_days)
                        ? user.work_days
                            .map((d) => weekdayMap[d] || d)
                            .join(", ")
                        : "Не указано"}
                    </div>
                  </div>
                  <div className={style.card_footer}>
                    <div>
                      <b>Рабочий день:</b> {user.start_work_time || "-"} -{" "}
                      {user.end_work_time || "-"}
                    </div>
                    <div>
                      <b>Пол:</b> {user.user_gender === "Male" ? "М" : "Ж"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <UserDetailModal
        visible={isUserModalVisible}
        onClose={closeUserModal}
        user={selectedUser}
      />
    </div>
  );
};

export default GalereyaUserList;
